// What an opening puzzle, and a game's opening, have to say for themselves.

import { Fragment, type ReactNode } from "react";
import { cap, moveLabel, moveNumberOf, pct } from "../app/format.ts";
import { follow } from "../app/router.ts";

/** "Bc5", "Bc5 or Nf6", "Bc5, Nf6 or d6", with the moves in bold. */
export function OrList({ sans }: { sans: string[] }) {
  return (
    <>
      {sans.map((san, i) => (
        <Fragment key={i}>
          {i > 0 ? (i === sans.length - 1 ? " or " : ", ") : null}
          <b>{san}</b>
        </Fragment>
      ))}
    </>
  );
}

interface BookMove {
  san: string;
  comment?: string;
  masters?: number;
  masters_share?: number;
  band_games?: number;
  band_share?: number;
  white_score?: number | null;
}

/** The theory, and its numbers. */
export function OpeningExplain({ puzzle, color }: { puzzle: { opening?: any; best_san: string; played_san: string }; color: string }) {
  const op = puzzle.opening || {};
  const answers: BookMove[] = op.answers || [];
  const sans = answers.map((a) => a.san);
  const stats = openingStats(op, puzzle, color);
  const comment = answers.find((a) => a.comment)?.comment;
  return (
    <>
      {op.source === "repertoire" ? (
        <>
          <p className="puzzle-why">
            Your repertoire has <OrList sans={sans.length ? sans : [puzzle.best_san]} /> here.
          </p>
          {comment ? <p className="puzzle-comment">{comment}</p> : null}
        </>
      ) : (
        <p className="puzzle-why">
          Theory here is <OrList sans={sans.length ? sans : [puzzle.best_san]} />.
        </p>
      )}
      {stats ? <p className="puzzle-stats">{stats}</p> : null}
    </>
  );
}

function openingStats(op: any, puzzle: { played_san: string }, color: string): string {
  const answers: BookMove[] = op.answers || [];
  const played: BookMove | undefined = op.played;
  const bits: string[] = [];
  // A share of one master game is 100% of nothing: the masters' numbers are
  // quoted only where the book counts them as theory, ten games or more.
  const mastersTotal =
    [...answers, played].filter((m): m is BookMove => Boolean(m && m.masters! > 0 && m.masters_share! > 0)).map((m) => m.masters! / m.masters_share!)[0] || 0;
  const masters = answers.filter((a) => a.masters! > 0);
  const score = () => {
    if (!played || !(played.band_games! > 0) || played.white_score == null) return "";
    // The band's results are White's; the sentence is about your side.
    const mine = color === "white" ? played.white_score : 1 - played.white_score;
    return `${played.san} scores ${pct(mine)} for ${cap(color)}`;
  };
  if (mastersTotal >= 10 && (masters.length || (played && played.masters! > 0))) {
    const parts = masters.map((a) => `${a.san} ${pct(a.masters_share)}`);
    if (played && played.masters! > 0) parts.push(`${played.san} ${pct(played.masters_share)}`);
    bits.push(`Masters: ${parts.join(" · ")}.`);
    if (score()) bits.push(`At your level ${score()}.`);
  } else if (answers.some((a) => a.band_games! > 0)) {
    const parts = answers.filter((a) => a.band_games! > 0).map((a) => `${a.san} ${pct(a.band_share)}`);
    if (played && played.band_games! > 0) parts.push(`${played.san} ${pct(played.band_share)}`);
    bits.push(`At your level: ${parts.join(" · ")}${score() ? `, and ${score()}` : ""}.`);
  } else if (score()) {
    bits.push(`At your level ${score()}.`);
  }
  if (!played) bits.push(`${puzzle.played_san} is not in the book.`);
  return bits.join(" ");
}

/** The line under a report's result: which opening, and where it was left. */
export function OpeningLine({ report }: { report: any }) {
  const op = report.opening;
  if (!op || !op.name) return null;
  const bits: ReactNode[] = [<span key="name" className="opening-name">{op.name}</span>];
  const sep = (key: string) => (
    <span key={key} className="faint sep">
      {" · "}
    </span>
  );
  const mistake =
    op.mistake_ply != null ? (report.puzzles || []).find((p: any) => p.ply === op.mistake_ply && p.kind === "opening") : null;
  if (op.book_plies > 0) bits.push(sep("s1"), <span key="book">in book until move {moveNumberOf(op.book_plies - 1)}</span>);
  const fromRepertoire = mistake?.opening?.source === "repertoire";
  if (mistake) {
    // A costly move can still be a book move — the game goes on in book past
    // it — so "left theory" is only said when the game actually did.
    const move = `${moveLabel(mistake.move_number, report.color)} ${mistake.played_san}`;
    const answers = (mistake.opening?.answers || []).map((a: BookMove) => a.san);
    const text = fromRepertoire
      ? `you left your line at ${move}`
      : mistake.ply >= op.book_plies
        ? `you left theory at ${move}`
        : `${move} cost you${answers.length ? ` — theory is ${answers.slice(0, 2).join(" or ")}` : ""}`;
    bits.push(sep("s2"), <span key="left" className="opening-left">{text}</span>);
  } else if (op.left_by === "you") {
    bits.push(sep("s2"), <span key="left">you left the book at move {moveNumberOf(op.book_plies)}</span>);
  } else if (op.left_by === "opponent") {
    bits.push(sep("s2"), <span key="left">your opponent left the book at move {moveNumberOf(op.book_plies)}</span>);
  }
  const rep = op.repertoire;
  if (rep) {
    bits.push(
      sep("s3"),
      <span key="rep">
        in your <a {...follow(`/openings/${rep.id}`)}>{rep.name}</a>
        {rep.followed_plies > 0 ? ` line until move ${moveNumberOf(rep.followed_plies - 1)}` : " line"}
      </span>
    );
    if (!fromRepertoire && rep.left_by === "you" && rep.played_san && (rep.prepared || []).length) {
      bits.push(sep("s4"), <span key="prep">you played {rep.played_san}, your line has {rep.prepared.join(" or ")}</span>);
    }
  }
  return <p className="opening-line">{bits}</p>;
}
